import { Box, Flex, Text, Tooltip, VStack } from '@chakra-ui/react';
import { faInfoCircle, faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, OutlineCard, Switch } from '@morressier/alembic';
import React from 'react';
import { WorkflowBucket } from '@publishing-engine/submission-service/src/endpoints/api.response-types';

export interface BucketToggleListProps {
  buckets: WorkflowBucket[];
  configuredBucketNames: Set<string>;
  configuredSubBucketsMap: Map<string, Set<string>>;
  savingBuckets: Set<string>;
  savingSubBuckets: Set<string>;
  expandedBuckets: Set<string>;
  onBucketToggle: (bucketName: string, enable: boolean) => void;
  onSubBucketToggle: (
    bucketName: string,
    subBucketName: string,
    enable: boolean,
  ) => void;
  onExpandToggle: (bucketName: string) => void;
}

const BucketToggleList: React.FC<BucketToggleListProps> = ({
  buckets,
  configuredBucketNames,
  configuredSubBucketsMap,
  savingBuckets,
  savingSubBuckets,
  expandedBuckets,
  onBucketToggle,
  onSubBucketToggle,
  onExpandToggle,
}) => (
  <OutlineCard borderless paddingX={4} paddingY={4}>
    <Flex direction="column" gap={3}>
      <Flex direction="column" gap={1}>
        <Text textStyle="headingItem">Available Workflows</Text>
        <Text textStyle="textCaption" color="textSecondary">
          Toggle workflows on or off. Click a workflow to expand and manage
          individual sub-workflows.
        </Text>
      </Flex>

      <Box width="100%">
        {buckets.map((bucket) => {
          const isConfigured = configuredBucketNames.has(
            bucket.bucketName,
          );
          const isExpanded = expandedBuckets.has(bucket.bucketName);
          const isBucketSaving = savingBuckets.has(bucket.bucketName);
          const configuredSubs =
            configuredSubBucketsMap.get(bucket.bucketName) ??
            new Set<string>();
          const hasSubBuckets =
            (bucket.subBuckets?.length ?? 0) > 0;

          return (
            <Box key={bucket.bucketId} mb="4">
              {/* ---- Bucket row ---- */}
              <Flex
                background="actionSecondaryFill"
                p={3}
                cursor={hasSubBuckets ? 'pointer' : 'default'}
                onClick={() => {
                  if (hasSubBuckets) {
                    onExpandToggle(bucket.bucketName);
                  }
                }}
              >
                <Flex
                  flex="1"
                  textAlign="left"
                  justify="space-between"
                  align="center"
                >
                  <Flex align="center" gap={3}>
                    <Switch
                      isChecked={isConfigured}
                      isDisabled={isBucketSaving}
                      onChange={(e) => {
                        e.stopPropagation();
                        onBucketToggle(
                          bucket.bucketName,
                          e.target.checked,
                        );
                      }}
                      aria-label={`Toggle ${bucket.bucketName}`}
                    />
                    <Text textStyle="headingItem" wordBreak="break-word">
                      {bucket.bucketName}
                    </Text>
                  </Flex>
                  {hasSubBuckets && (
                    <Box ml={2}>
                      <FontAwesomeIcon
                        icon={isExpanded ? faMinus : faPlus}
                        color="blue"
                      />
                    </Box>
                  )}
                </Flex>
              </Flex>

              {/* ---- Sub-bucket toggles ---- */}
              {isExpanded && hasSubBuckets && (
                <Box p={3} borderRadius="md" bg="white">
                  <VStack align="stretch" spacing={1}>
                    {bucket.subBuckets.map((sub) => {
                      const subKey = `${bucket.bucketName}::${sub.subBucketName}`;
                      const isSubConfigured = configuredSubs.has(
                        sub.subBucketName,
                      );
                      const isSubSaving =
                        savingSubBuckets.has(subKey);

                      return (
                        <Flex
                          key={subKey}
                          align="center"
                          gap={2}
                        >
                          <Switch
                            isChecked={isSubConfigured}
                            isDisabled={isSubSaving}
                            onChange={(e) => {
                              onSubBucketToggle(
                                bucket.bucketName,
                                sub.subBucketName,
                                e.target.checked,
                              );
                            }}
                            aria-label={`Toggle ${sub.subBucketName}`}
                          />
                          <Text
                            justifyContent="flex-start"
                            whiteSpace="normal"
                            textAlign="left"
                            color="primaryText"
                          >
                            {sub.subBucketName}
                          </Text>
                          {sub.definition && (
                            <Tooltip
                              label={sub.definition || ''}
                              hasArrow
                              placement="auto"
                            >
                              <Box as="span" cursor="pointer">
                                <ButtonIcon
                                  size="small"
                                  ariaLabel="Journal bucket info icon"
                                  icon={
                                    <FontAwesomeIcon
                                      icon={faInfoCircle}
                                    />
                                  }
                                  marginBottom="2px"
                                />
                              </Box>
                            </Tooltip>
                          )}
                        </Flex>
                      );
                    })}
                  </VStack>
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </Flex>
  </OutlineCard>
);

export default BucketToggleList;
